import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Globe, ArrowRight, Users, Target, Heart } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const team = [
  /*{
    name: "Andreu Prio",
    role: "CEO",
    bio: "Experienced entrepreneur in EdTech and language learning. Speaks 6 languages.",
    photo: "/images/team/andreu.jpg",
  },*/
  {
    name: "Aitor Frias",
    role: "CEO & Co-founder",
    bio: "Telecom and Aeronautical Engineer from UPC and entrepreneur. Speaks 8 languages.",
    photo: "/images/team/aitor.jpg",
  },
  {
    name: "David Pereda",
    role: "Lead Developer",
    bio: "Full-stack engineer passionate about scalable architectures and language technologies.",
    photo: "/images/team/david.jpg",
  },
  {
    name: "Tetiana Pronikova",
    role: "Marketing & Sales",
    bio: "Expert in B2B growth strategies and international market expansion.",
    photo: "/images/team/tetiana.jpg",
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      <section className="pt-28 pb-16 bg-surface border-b border-border">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">
            Our Story
          </p>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-5">
            Language is the Bridge Between Cultures
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed">
            Lingua2 was founded on a simple belief: the most effective way to
            learn a language is to use it in real conversations with real
            people. We built the technology to make that happen at scale.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              {
                icon: Target,
                title: "Our Mission",
                desc: "To break down language barriers for organizations worldwide, enabling authentic human communication through technology and peer exchange.",
              },
              {
                icon: Globe,
                title: "Our Vision",
                desc: "A world where language is never a barrier to collaboration, understanding, or opportunity — for every organization, of every size.",
              },
              {
                icon: Heart,
                title: "Our Values",
                desc: "Authenticity, inclusivity, and measurable impact. Every language — from Mandarin to Basque — deserves a platform and a community.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-7 rounded-xl border border-border bg-card"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4">
                  <item.icon
                    className="w-5 h-5 text-primary"
                    strokeWidth={1.75}
                  />
                </div>
                <h3 className="text-base font-display font-bold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-surface">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">
            Our Journey
          </p>
          <h2 className="text-2xl font-display font-bold text-foreground mb-5">
            Built from a Language Learner's Frustration
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              Lingua2 was created after our founder experienced firsthand how
              inadequate traditional language learning tools were for real
              professional communication. The missing piece was always the same:
              authentic practice with real people.
            </p>
            <p>
              The language exchange method had existed for decades, but it was
              fragmented, unscalable, and impossible to measure. We set out to
              change that — building a platform to connect thousands of learners
              simultaneously, backed by AI that makes every session more
              productive.
            </p>
            <p>
              Today, Lingua2 serves 500+ organizations in 60+ countries. Our
              platform hosts 2M+ language sessions per year across 8,000+
              languages — including dozens of endangered ones we are proud to
              help preserve.
            </p>
          </div>
          <div className="mt-8">
            <Button
              asChild
              className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast"
            >
              <Link to="/contact">
                Get in Touch <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Leadership" title="The Team Behind Lingua2" />
          <div className="flex flex-wrap justify-center gap-5">
            {team.map((m) => (
              <div
                key={m.name}
                className="w-64 p-6 rounded-xl border border-border bg-card text-center card-hover"
              >
                <img
                  src={m.photo}
                  alt={m.name}
                  className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-2 border-primary"
                />
                <h3 className="text-sm font-display font-bold text-foreground mb-0.5">
                  {m.name}
                </h3>
                <p className="text-xs text-primary font-medium mb-2">
                  {m.role}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {m.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 bg-primary">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: "2025", label: "Founded" },
              { value: "60+", label: "Countries" },
              { value: "500+", label: "Organizations" },
              { value: "11", label: "Team Members" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-display font-bold text-primary-foreground mb-1">
                  {s.value}
                </div>
                <div className="text-sm text-primary-foreground/70">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-background text-center">
        <div className="max-w-lg mx-auto px-4">
          <h2 className="text-2xl font-display font-bold text-foreground mb-3">
            Want to Learn More?
          </h2>
          <p className="text-muted-foreground text-sm mb-7">
            Speak to our team about how Lingua2 can serve your organization.
          </p>
          <Button
            size="lg"
            asChild
            className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast"
          >
            <Link to="/contact">
              Contact Us <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
